import Cookie from 'js-cookie';
import { isBefore } from 'date-fns';
import { AUTH_TOKEN_KEY, DEFAULT_LOCALE, SUPPORTED_LOCALES } from './constants';

export const getAuthToken = (tokenKey: string = 'tuempresaenestonia_auth'): any => {
  const authCookie = Cookie.get(tokenKey);
  if (authCookie) {
    const parsedCookie = JSON.parse(authCookie);
    if (
      parsedCookie &&
      parsedCookie.expires &&
      parsedCookie.access_token &&
      isBefore(new Date(), new Date(parsedCookie.expires))
    ) {
      return parsedCookie.access_token;
    }
  }
  return null;
};

export const readAuthToken = () => {
  return window.localStorage.getItem(AUTH_TOKEN_KEY) || '';
};

export const capitalizeFirstLetter = (s: string) => {
  return (s && s.charAt(0).toUpperCase() + s.slice(1)) || '';
};

export const capitalizeFirstLetterOfEachWord = (input: string) => {
  const words = input.split(' ');

  const capitalizedWords = words.map((word) => {
    if (word.length > 0) {
      return capitalizeFirstLetter(word);
    }
    return word;
  });

  return capitalizedWords.join(' ');
};

export const removeSnakeCase = (string: string) => {
  return string.replace(/_/g, ' ');
};

export function fileToBase64(
  file: File,
  // eslint-disable-next-line no-unused-vars
  callback: (result: string | ArrayBuffer | null) => void,
  cropSize?: number
) {
  const reader = new FileReader();
  reader.onload = (event: ProgressEvent<FileReader>) => {
    if (cropSize && cropSize > 0) {
      cropAndResizeImageToMaxCropSize(event, cropSize, (dataUrl) => callback(dataUrl));
    } else if (event.target?.result) callback(event.target.result);
  };
  reader.readAsDataURL(file);
}

export function cropAndResizeImageToMaxCropSize(
  event: ProgressEvent<FileReader>,
  cropSize: number,
  // eslint-disable-next-line no-unused-vars
  callback: (dataUrl: string) => void
) {
  const image = new Image();
  image.onload = () => {
    // Calculate the cropping/resizing properties of the image
    const canvas = document.createElement('canvas');
    const { width } = image;
    const { height } = image;
    let minSide = 0;
    let maxSide = 0;
    let startX = 0;
    let startY = 0;
    const widthStretch = cropSize;
    const heightStretch = cropSize;
    if (width > height) {
      maxSide = width;
      minSide = height;
      startX = (maxSide - minSide) / 2;
      startY = 0;
    } else {
      maxSide = height;
      minSide = width;
      startX = 0;
      startY = (maxSide - minSide) / 2;
    }
    // set in resized-cropped canvas
    canvas.width = cropSize;
    canvas.height = cropSize;
    canvas.getContext('2d')?.drawImage(
      image, // image
      startX,
      startY, // start position in the original image, depending on aspect ratio
      minSide,
      minSide, // dimensions to "cut" from the original image
      0,
      0, // starting position in final canvas
      widthStretch,
      heightStretch // dimensions to "paste" in final canvas
    );
    const dataUrl = canvas.toDataURL('image/jpeg');
    callback(dataUrl);
  };
  image.setAttribute('src', String(event.target?.result));
}

export function downloadDocument(docId: string) {
  let endpoint = `/docs/${docId}/download`;
  endpoint += `?Authorization=${readAuthToken()}`;
  window.open(process.env.REACT_APP_API_URL + endpoint, '_blank');
}

export const getLocale = (queryParams: URLSearchParams): string => {
  const paramLocale = queryParams.get('locale')?.split(/[-_]/)[0];
  if (paramLocale && SUPPORTED_LOCALES.includes(paramLocale)) {
    return paramLocale;
  }

  const browserLocale = navigator.language.split(/[-_]/)[0];
  return SUPPORTED_LOCALES.includes(browserLocale) ? browserLocale : DEFAULT_LOCALE;
};

export const getInitialLocale = (): string =>
  getLocale(new URLSearchParams(window.location.search));
